import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { 
  Car, 
  LogOut, 
  Users, 
  UserCheck, 
  DollarSign, 
  Clock,
  Eye,
  Trash2,
  RefreshCw,
  Phone,
  Mail,
  MapPin,
  Repeat,
  FileText
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const AdminDashboard = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('leads');
  const [leads, setLeads] = useState([]);
  const [referrals, setReferrals] = useState([]);
  const [tradeins, setTradeins] = useState([]);
  const [stats, setStats] = useState({ 
    total: 0, new: 0, contacted: 0, sold: 0, today: 0,
    referrals: { total: 0, new: 0, today: 0 },
    tradeins: { total: 0, new: 0, today: 0 }
  });
  const [isLoading, setIsLoading] = useState(true);
  const [selectedItem, setSelectedItem] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [dialogType, setDialogType] = useState('lead');

  const getAuthHeader = useCallback(() => {
    const auth = sessionStorage.getItem('adminAuth');
    if (!auth) {
      navigate('/admin');
      return null;
    }
    return { Authorization: `Basic ${auth}` };
  }, [navigate]);

  const fetchData = useCallback(async () => {
    const headers = getAuthHeader();
    if (!headers) return;

    setIsLoading(true);
    try {
      const [leadsRes, referralsRes, tradeinsRes, statsRes] = await Promise.all([
        axios.get(`${API}/leads`, { headers }),
        axios.get(`${API}/referrals`, { headers }),
        axios.get(`${API}/trade-ins`, { headers }),
        axios.get(`${API}/stats`, { headers })
      ]);
      setLeads(leadsRes.data);
      setReferrals(referralsRes.data);
      setTradeins(tradeinsRes.data);
      setStats(statsRes.data);
    } catch (error) {
      console.error("Fetch error:", error);
      if (error.response?.status === 401) {
        sessionStorage.removeItem('adminAuth');
        navigate('/admin');
        toast.error("Session expired. Please login again.");
      } else {
        toast.error("Failed to fetch data");
      }
    } finally {
      setIsLoading(false);
    }
  }, [getAuthHeader, navigate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleLogout = () => {
    sessionStorage.removeItem('adminAuth');
    navigate('/admin');
    toast.success("Logged out successfully");
  };

  const handleStatusChange = async (itemId, newStatus, type) => {
    const headers = getAuthHeader();
    if (!headers) return;

    const endpoint = type === 'lead' ? 'leads' : type === 'referral' ? 'referrals' : 'trade-ins';

    try {
      await axios.patch(`${API}/${endpoint}/${itemId}/status`, { status: newStatus }, { headers });
      toast.success("Status updated");
      fetchData();
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  const handleDelete = async (itemId, type) => {
    if (!window.confirm("Are you sure you want to delete this item?")) return;
    
    const headers = getAuthHeader();
    if (!headers) return;

    const endpoint = type === 'lead' ? 'leads' : type === 'referral' ? 'referrals' : 'trade-ins';

    try {
      await axios.delete(`${API}/${endpoint}/${itemId}`, { headers });
      toast.success("Item deleted");
      setIsDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error("Failed to delete item");
    }
  };

  const viewItem = (item, type) => {
    setSelectedItem(item);
    setDialogType(type);
    setIsDialogOpen(true);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatBudget = (value) => {
    return new Intl.NumberFormat('en-CA', {
      style: 'currency',
      currency: 'CAD',
      maximumFractionDigits: 0
    }).format(value);
  };

  const getStatusClass = (status) => {
    switch (status) {
      case 'new': return 'status-new';
      case 'contacted': return 'status-contacted';
      case 'sold': return 'status-sold';
      case 'closed': return 'status-closed';
      default: return 'status-new';
    }
  };

  const renderLeadsTable = () => (
    <div className="table-container">
      <table className="leads-table" data-testid="leads-table">
        <thead>
          <tr>
            <th>Name</th>
            <th>Contact</th>
            <th>Vehicle</th>
            <th>Budget</th>
            <th>Status</th>
            <th>Date</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {leads.map((lead) => (
            <tr key={lead.id} data-testid={`lead-row-${lead.id}`}>
              <td data-label="Name">
                <strong>{lead.full_name}</strong>
              </td>
              <td data-label="Contact">
                <div style={{ fontSize: '0.8rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                    <Phone size={12} /> {lead.phone}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', color: '#64748B' }}>
                    <Mail size={12} /> {lead.email}
                  </div>
                </div>
              </td>
              <td data-label="Vehicle">
                <span style={{ textTransform: 'capitalize' }}>{lead.car_condition} {lead.vehicle_type}</span>
                {lead.make_model && <div style={{ fontSize: '0.75rem', color: '#64748B' }}>{lead.make_model}</div>}
              </td>
              <td data-label="Budget">
                <strong style={{ color: '#065F46' }}>{formatBudget(lead.budget)}</strong>
              </td>
              <td data-label="Status">
                <select
                  className={`status-badge ${getStatusClass(lead.status)}`}
                  value={lead.status}
                  onChange={(e) => handleStatusChange(lead.id, e.target.value, 'lead')}
                  style={{ cursor: 'pointer', border: 'none' }}
                  data-testid={`status-select-${lead.id}`}
                >
                  <option value="new">New</option>
                  <option value="contacted">Contacted</option>
                  <option value="sold">Sold</option>
                  <option value="closed">Closed</option>
                </select>
              </td>
              <td data-label="Date">
                {formatDate(lead.created_at)}
              </td>
              <td data-label="Actions">
                <button 
                  className="action-btn action-view"
                  onClick={() => viewItem(lead, 'lead')}
                  data-testid={`view-lead-${lead.id}`}
                >
                  <Eye size={14} />
                </button>
                <button 
                  className="action-btn action-delete"
                  onClick={() => handleDelete(lead.id, 'lead')}
                  data-testid={`delete-lead-${lead.id}`}
                >
                  <Trash2 size={14} />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  const renderReferralsTable = () => (
    <div className="table-container">
      <table className="leads-table" data-testid="referrals-table">
        <thead>
          <tr>
            <th>Friend</th>
            <th>Referred By</th>
            <th>Location</th>
            <th>Interest</th>
            <th>Status</th>
            <th>Date</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {referrals.map((ref) => (
            <tr key={ref.id} data-testid={`referral-row-${ref.id}`}>
              <td data-label="Friend">
                <div className="referral-info">
                  <strong>{ref.friend_name}</strong>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                    <Phone size={12} /> {ref.friend_phone}
                  </div>
                </div>
              </td>
              <td data-label="Referred By">
                <div className="referral-info">
                  <span>{ref.referrer_name}</span>
                  <div className="referrer">{ref.referrer_phone}</div>
                </div>
              </td>
              <td data-label="Location">
                {ref.friend_community}, {ref.friend_province}
              </td>
              <td data-label="Interest">
                {ref.vehicle_interest || '-'}
              </td>
              <td data-label="Status">
                <select
                  className={`status-badge ${getStatusClass(ref.status)}`}
                  value={ref.status}
                  onChange={(e) => handleStatusChange(ref.id, e.target.value, 'referral')}
                  style={{ cursor: 'pointer', border: 'none' }}
                >
                  <option value="new">New</option>
                  <option value="contacted">Contacted</option>
                  <option value="sold">Sold</option>
                  <option value="closed">Closed</option>
                </select>
              </td>
              <td data-label="Date">
                {formatDate(ref.created_at)}
              </td>
              <td data-label="Actions">
                <button 
                  className="action-btn action-view"
                  onClick={() => viewItem(ref, 'referral')}
                >
                  <Eye size={14} />
                </button>
                <button 
                  className="action-btn action-delete"
                  onClick={() => handleDelete(ref.id, 'referral')}
                >
                  <Trash2 size={14} />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  const renderTradeInsTable = () => (
    <div className="table-container">
      <table className="leads-table" data-testid="tradeins-table">
        <thead>
          <tr>
            <th>Contact</th>
            <th>Trade-In Vehicle</th>
            <th>Mileage</th>
            <th>Condition</th>
            <th>Status</th>
            <th>Date</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {tradeins.map((ti) => (
            <tr key={ti.id} data-testid={`tradein-row-${ti.id}`}>
              <td data-label="Contact">
                <div className="referral-info">
                  <strong>{ti.full_name}</strong>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                    <Phone size={12} /> {ti.phone}
                  </div>
                </div>
              </td>
              <td data-label="Vehicle">
                <div className="tradein-vehicle">
                  {ti.vehicle_year} {ti.vehicle_make} {ti.vehicle_model}
                </div>
              </td>
              <td data-label="Mileage">
                {ti.mileage.toLocaleString()} km
              </td>
              <td data-label="Condition">
                <div className="tradein-details">
                  {ti.condition.split(' - ')[0]}
                </div>
              </td>
              <td data-label="Status">
                <select
                  className={`status-badge ${getStatusClass(ti.status)}`}
                  value={ti.status}
                  onChange={(e) => handleStatusChange(ti.id, e.target.value, 'tradein')}
                  style={{ cursor: 'pointer', border: 'none' }}
                >
                  <option value="new">New</option>
                  <option value="contacted">Contacted</option>
                  <option value="sold">Sold</option>
                  <option value="closed">Closed</option>
                </select>
              </td>
              <td data-label="Date">
                {formatDate(ti.created_at)}
              </td>
              <td data-label="Actions">
                <button 
                  className="action-btn action-view"
                  onClick={() => viewItem(ti, 'tradein')}
                >
                  <Eye size={14} />
                </button>
                <button 
                  className="action-btn action-delete"
                  onClick={() => handleDelete(ti.id, 'tradein')}
                >
                  <Trash2 size={14} />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  const renderLeadDialog = () => (
    <div className="lead-detail">
      <div className="lead-detail-row">
        <span className="lead-detail-label">Name</span>
        <span className="lead-detail-value">{selectedItem.full_name}</span>
      </div>
      <div className="lead-detail-row">
        <span className="lead-detail-label">Phone</span>
        <span className="lead-detail-value">
          <a href={`tel:${selectedItem.phone}`} style={{ color: '#EA580C' }}>{selectedItem.phone}</a>
        </span>
      </div>
      <div className="lead-detail-row">
        <span className="lead-detail-label">Email</span>
        <span className="lead-detail-value">
          <a href={`mailto:${selectedItem.email}`} style={{ color: '#EA580C' }}>{selectedItem.email}</a>
        </span>
      </div>
      <div className="lead-detail-row">
        <span className="lead-detail-label">Address</span>
        <span className="lead-detail-value">
          {selectedItem.street_address}, {selectedItem.city}, {selectedItem.state} {selectedItem.zip_code}
        </span>
      </div>
      <div className="lead-detail-row">
        <span className="lead-detail-label">Vehicle</span>
        <span className="lead-detail-value" style={{ textTransform: 'capitalize' }}>
          {selectedItem.car_condition} {selectedItem.vehicle_type}
          {selectedItem.make_model && ` - ${selectedItem.make_model}`}
        </span>
      </div>
      <div className="lead-detail-row">
        <span className="lead-detail-label">Budget</span>
        <span className="lead-detail-value" style={{ fontWeight: '600', color: '#065F46' }}>
          {formatBudget(selectedItem.budget)}
        </span>
      </div>
      <div className="lead-detail-row">
        <span className="lead-detail-label">Date of Birth</span>
        <span className="lead-detail-value">{selectedItem.date_of_birth}</span>
      </div>
      <div className="lead-detail-row">
        <span className="lead-detail-label">Housing</span>
        <span className="lead-detail-value">
          {selectedItem.home_ownership} - ${selectedItem.monthly_payment}/mo ({selectedItem.residency_length})
        </span>
      </div>
      <div className="lead-detail-row">
        <span className="lead-detail-label">Income</span>
        <span className="lead-detail-value">
          {selectedItem.income_source} ({selectedItem.income_length})
        </span>
      </div>
      <div className="lead-detail-row">
        <span className="lead-detail-label">Submitted</span>
        <span className="lead-detail-value">{formatDate(selectedItem.created_at)}</span>
      </div>
      
      <div style={{ marginTop: '1.5rem', display: 'flex', gap: '0.5rem' }}>
        <a 
          href={`tel:${selectedItem.phone}`}
          className="btn btn-primary"
          style={{ flex: 1, textDecoration: 'none' }}
        >
          <Phone size={16} /> Call Now
        </a>
        <button 
          className="btn btn-secondary"
          onClick={() => handleDelete(selectedItem.id, 'lead')}
          style={{ padding: '0.75rem' }}
        >
          <Trash2 size={16} />
        </button>
      </div>
    </div>
  );

  const renderReferralDialog = () => (
    <div className="lead-detail">
      <h3 style={{ fontSize: '1rem', color: '#EA580C', marginBottom: '1rem' }}>Referred Friend</h3>
      <div className="lead-detail-row">
        <span className="lead-detail-label">Name</span>
        <span className="lead-detail-value">{selectedItem.friend_name}</span>
      </div>
      <div className="lead-detail-row">
        <span className="lead-detail-label">Phone</span>
        <span className="lead-detail-value">
          <a href={`tel:${selectedItem.friend_phone}`} style={{ color: '#EA580C' }}>{selectedItem.friend_phone}</a>
        </span>
      </div>
      <div className="lead-detail-row">
        <span className="lead-detail-label">Email</span>
        <span className="lead-detail-value">
          {selectedItem.friend_email ? (
            <a href={`mailto:${selectedItem.friend_email}`} style={{ color: '#EA580C' }}>{selectedItem.friend_email}</a>
          ) : 'Not provided'}
        </span>
      </div>
      <div className="lead-detail-row">
        <span className="lead-detail-label">Location</span>
        <span className="lead-detail-value">{selectedItem.friend_community}, {selectedItem.friend_province}</span>
      </div>
      <div className="lead-detail-row">
        <span className="lead-detail-label">Vehicle Interest</span>
        <span className="lead-detail-value">{selectedItem.vehicle_interest || 'Not specified'}</span>
      </div>
      
      <h3 style={{ fontSize: '1rem', color: '#EA580C', marginTop: '1.5rem', marginBottom: '1rem' }}>Referrer</h3>
      <div className="lead-detail-row">
        <span className="lead-detail-label">Name</span>
        <span className="lead-detail-value">{selectedItem.referrer_name}</span>
      </div>
      <div className="lead-detail-row">
        <span className="lead-detail-label">Phone</span>
        <span className="lead-detail-value">{selectedItem.referrer_phone}</span>
      </div>
      <div className="lead-detail-row">
        <span className="lead-detail-label">Email</span>
        <span className="lead-detail-value">{selectedItem.referrer_email}</span>
      </div>
      <div className="lead-detail-row">
        <span className="lead-detail-label">Submitted</span>
        <span className="lead-detail-value">{formatDate(selectedItem.created_at)}</span>
      </div>
      
      <div style={{ marginTop: '1.5rem', display: 'flex', gap: '0.5rem' }}>
        <a 
          href={`tel:${selectedItem.friend_phone}`}
          className="btn btn-primary"
          style={{ flex: 1, textDecoration: 'none' }}
        >
          <Phone size={16} /> Call Friend
        </a>
        <button 
          className="btn btn-secondary"
          onClick={() => handleDelete(selectedItem.id, 'referral')}
          style={{ padding: '0.75rem' }}
        >
          <Trash2 size={16} />
        </button>
      </div>
    </div>
  );

  const renderTradeInDialog = () => (
    <div className="lead-detail">
      <h3 style={{ fontSize: '1rem', color: '#EA580C', marginBottom: '1rem' }}>Contact</h3>
      <div className="lead-detail-row">
        <span className="lead-detail-label">Name</span>
        <span className="lead-detail-value">{selectedItem.full_name}</span>
      </div>
      <div className="lead-detail-row">
        <span className="lead-detail-label">Phone</span>
        <span className="lead-detail-value">
          <a href={`tel:${selectedItem.phone}`} style={{ color: '#EA580C' }}>{selectedItem.phone}</a>
        </span>
      </div>
      <div className="lead-detail-row">
        <span className="lead-detail-label">Email</span>
        <span className="lead-detail-value">
          <a href={`mailto:${selectedItem.email}`} style={{ color: '#EA580C' }}>{selectedItem.email}</a>
        </span>
      </div>
      <div className="lead-detail-row">
        <span className="lead-detail-label">Location</span>
        <span className="lead-detail-value">{selectedItem.community}, {selectedItem.province}</span>
      </div>
      
      <h3 style={{ fontSize: '1rem', color: '#EA580C', marginTop: '1.5rem', marginBottom: '1rem' }}>Trade-In Vehicle</h3>
      <div className="lead-detail-row">
        <span className="lead-detail-label">Vehicle</span>
        <span className="lead-detail-value">{selectedItem.vehicle_year} {selectedItem.vehicle_make} {selectedItem.vehicle_model}</span>
      </div>
      <div className="lead-detail-row">
        <span className="lead-detail-label">Mileage</span>
        <span className="lead-detail-value">{selectedItem.mileage.toLocaleString()} km</span>
      </div>
      <div className="lead-detail-row">
        <span className="lead-detail-label">Condition</span>
        <span className="lead-detail-value">{selectedItem.condition}</span>
      </div>
      <div className="lead-detail-row">
        <span className="lead-detail-label">Balance Owing</span>
        <span className="lead-detail-value">
          {selectedItem.balance_owing ? formatBudget(selectedItem.balance_owing) : 'None'}
        </span>
      </div>
      {selectedItem.vehicle_preference && (
        <div className="lead-detail-row">
          <span className="lead-detail-label">Looking For</span>
          <span className="lead-detail-value">{selectedItem.vehicle_preference}</span>
        </div>
      )}
      {selectedItem.notes && (
        <div className="lead-detail-row">
          <span className="lead-detail-label">Notes</span>
          <span className="lead-detail-value">{selectedItem.notes}</span>
        </div>
      )}
      <div className="lead-detail-row">
        <span className="lead-detail-label">Submitted</span>
        <span className="lead-detail-value">{formatDate(selectedItem.created_at)}</span>
      </div>
      
      <div style={{ marginTop: '1.5rem', display: 'flex', gap: '0.5rem' }}>
        <a 
          href={`tel:${selectedItem.phone}`}
          className="btn btn-primary"
          style={{ flex: 1, textDecoration: 'none' }}
        >
          <Phone size={16} /> Call Now
        </a>
        <button 
          className="btn btn-secondary"
          onClick={() => handleDelete(selectedItem.id, 'tradein')}
          style={{ padding: '0.75rem' }}
        >
          <Trash2 size={16} />
        </button>
      </div>
    </div>
  );

  const getCurrentData = () => {
    switch (activeTab) {
      case 'leads': return leads;
      case 'referrals': return referrals;
      case 'tradeins': return tradeins;
      default: return [];
    }
  };

  return (
    <div className="admin-container">
      {/* Header */}
      <header className="admin-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <Car size={24} />
          <h1>Admin Dashboard</h1>
        </div>
        <button 
          onClick={handleLogout}
          className="btn btn-secondary"
          style={{ padding: '0.5rem 1rem' }}
          data-testid="logout-button"
        >
          <LogOut size={16} />
          Logout
        </button>
      </header>

      {/* Main Content */}
      <main className="admin-main">
        {/* Stats */}
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-label">
              <FileText size={16} style={{ display: 'inline', marginRight: '0.5rem' }} />
              Applications
            </div>
            <div className="stat-value" data-testid="stat-total">{stats.total}</div>
            <div style={{ fontSize: '0.75rem', color: '#64748B' }}>{stats.new} new</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">
              <Users size={16} style={{ display: 'inline', marginRight: '0.5rem' }} />
              Referrals
            </div>
            <div className="stat-value" style={{ color: '#1E40AF' }} data-testid="stat-referrals">
              {stats.referrals?.total || 0}
            </div>
            <div style={{ fontSize: '0.75rem', color: '#64748B' }}>{stats.referrals?.new || 0} new</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">
              <Repeat size={16} style={{ display: 'inline', marginRight: '0.5rem' }} />
              Trade-Ins
            </div>
            <div className="stat-value" style={{ color: '#92400E' }} data-testid="stat-tradeins">
              {stats.tradeins?.total || 0}
            </div>
            <div style={{ fontSize: '0.75rem', color: '#64748B' }}>{stats.tradeins?.new || 0} new</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">
              <DollarSign size={16} style={{ display: 'inline', marginRight: '0.5rem' }} />
              Sold
            </div>
            <div className="stat-value" style={{ color: '#065F46' }} data-testid="stat-sold">{stats.sold}</div>
          </div>
        </div>

        {/* Tabs */}
        <div className="admin-tabs">
          <button 
            className={`admin-tab ${activeTab === 'leads' ? 'active' : ''}`}
            onClick={() => setActiveTab('leads')}
            data-testid="tab-leads"
          >
            <FileText size={16} />
            Applications
            <span className="tab-badge">{leads.length}</span>
          </button>
          <button 
            className={`admin-tab ${activeTab === 'referrals' ? 'active' : ''}`}
            onClick={() => setActiveTab('referrals')}
            data-testid="tab-referrals"
          >
            <Users size={16} />
            Referrals
            <span className="tab-badge">{referrals.length}</span>
          </button>
          <button 
            className={`admin-tab ${activeTab === 'tradeins' ? 'active' : ''}`}
            onClick={() => setActiveTab('tradeins')}
            data-testid="tab-tradeins"
          >
            <Repeat size={16} />
            Trade-Ins
            <span className="tab-badge">{tradeins.length}</span>
          </button>
        </div>

        {/* Data Table */}
        <div className="leads-card">
          <div className="leads-header">
            <h2>
              {activeTab === 'leads' && 'All Applications'}
              {activeTab === 'referrals' && 'All Referrals'}
              {activeTab === 'tradeins' && 'All Trade-In Enquiries'}
            </h2>
            <button 
              onClick={fetchData}
              className="btn btn-secondary"
              style={{ padding: '0.5rem 1rem' }}
              disabled={isLoading}
              data-testid="refresh-button"
            >
              <RefreshCw size={16} className={isLoading ? 'animate-spin' : ''} />
              Refresh
            </button>
          </div>

          {isLoading ? (
            <div className="empty-state">
              <RefreshCw size={32} className="animate-spin" />
              <p>Loading...</p>
            </div>
          ) : getCurrentData().length === 0 ? (
            <div className="empty-state">
              {activeTab === 'leads' && <FileText size={48} />}
              {activeTab === 'referrals' && <Users size={48} />}
              {activeTab === 'tradeins' && <Repeat size={48} />}
              <p>
                {activeTab === 'leads' && 'No applications yet.'}
                {activeTab === 'referrals' && 'No referrals yet.'}
                {activeTab === 'tradeins' && 'No trade-in enquiries yet.'}
              </p>
            </div>
          ) : (
            <>
              {activeTab === 'leads' && renderLeadsTable()}
              {activeTab === 'referrals' && renderReferralsTable()}
              {activeTab === 'tradeins' && renderTradeInsTable()}
            </>
          )}
        </div>
      </main>

      {/* Detail Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              {dialogType === 'lead' && <><Car size={20} /> Application Details</>}
              {dialogType === 'referral' && <><Users size={20} /> Referral Details</>}
              {dialogType === 'tradein' && <><Repeat size={20} /> Trade-In Details</>}
            </DialogTitle>
          </DialogHeader>
          
          {selectedItem && (
            <>
              {dialogType === 'lead' && renderLeadDialog()}
              {dialogType === 'referral' && renderReferralDialog()}
              {dialogType === 'tradein' && renderTradeInDialog()}
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDashboard;
